import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaFlask,
  FaRobot,
  FaShieldAlt,
  FaTimesCircle,
} from 'react-icons/fa';
import NavigationBar from './components/NavigationBar';
import LoadingSpinner from './components/LoadingSpinner';
import { BotIdentity } from './components/BotIdentity';
import { BotLiveActivity, useBotLiveActivity } from './components/BotLiveActivity';
import PublicDisclosureAlerts from './PublicDisclosureAlerts';
import TradeExplanation from './components/TradeExplanation';
import { AuthContext, axiosInstance } from './AuthContext';
import { isTruthfullyWatching } from './botReadiness';
import { strategyDisplayName } from './beginnerEducation';
import {
  MARKET_DATA_ACCESS,
  botRequiresMarketData,
  marketDataBanner,
  presentTodayBotReadiness,
} from './marketDataAttention';
import { archivedPracticePositions, practicePortfolioOverview } from './practicePortfolioPresentation';
import { resolveDecidedProposal } from './todayAttentionResolution';
import {
  CUSTOMER_ATTENTION_SEVERITY,
  customerAttentionPresentation,
} from './todayAttentionSeverity';

const money = (value, currency = 'USD') => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: /^[A-Z]{3}$/.test(currency || '') ? currency : 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const recordedUtc = (value) => {
  if (!value || !Number.isFinite(Date.parse(value))) return 'timestamp unavailable';
  return `${new Date(value).toLocaleString('en-US', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' })} UTC`;
};

const practiceActionLabel = (proposal) => ({
  enter_long: 'BUY',
  exit_long: 'SELL',
  enter_short: 'OPEN SHORT',
  exit_short: 'COVER SHORT',
}[proposal?.semantic_action] || proposal?.side || 'PRACTICE ACTION');

const practiceSignalTitle = (proposal) => (
  proposal?.semantic_action === 'enter_short'
    ? `SHORT SETUP FOUND · ${proposal.symbol}`
    : proposal?.semantic_action === 'exit_short'
      ? `COVER RULES MATCHED · ${proposal.symbol}`
      : `${proposal?.bot_name || 'Your bot'} found a Practice setup in ${proposal.symbol}.`
);

const PRACTICE_QUANTITY_SCALE = 1000000n;

const parsePracticeQuantity = (value) => {
  const match = String(value ?? '').trim().match(/^(\d+)(?:\.(\d*))?$/);
  if (!match) return null;
  const fraction = match[2] || '';
  if (fraction.length > 6 && /[1-9]/.test(fraction.slice(6))) return null;
  const micros = (BigInt(match[1]) * PRACTICE_QUANTITY_SCALE)
    + BigInt((fraction.slice(0, 6) || '').padEnd(6, '0'));
  return {
    micros,
    canonical: `${BigInt(match[1]).toString()}.${(fraction.slice(0, 6) || '').padEnd(6, '0')}`,
  };
};

const practiceShares = (value) => {
  const quantity = parsePracticeQuantity(value);
  if (!quantity) return '—';
  const whole = quantity.micros / PRACTICE_QUANTITY_SCALE;
  const fraction = (quantity.micros % PRACTICE_QUANTITY_SCALE).toString().padStart(6, '0');
  return `${whole.toLocaleString()}.${fraction}`;
};

const remainingPracticeShares = (position, close) => {
  const current = parsePracticeQuantity(position);
  const selected = parsePracticeQuantity(close);
  if (!current || !selected || selected.micros > current.micros) return null;
  const residual = current.micros - selected.micros;
  return `${residual / PRACTICE_QUANTITY_SCALE}.${(residual % PRACTICE_QUANTITY_SCALE).toString().padStart(6, '0')}`;
};

const decisionWords = (proposal) => {
  if (proposal?.semantic_action === 'exit_short') return { noun: 'cover', approved: 'Cover approved', rejected: 'Cover rejected' };
  if (proposal?.semantic_action === 'exit_long') return { noun: 'exit', approved: 'Exit approved', rejected: 'Exit rejected' };
  if (proposal?.semantic_action === 'enter_short') return { noun: 'Practice short entry', approved: 'Practice short entry approved', rejected: 'Practice short entry rejected' };
  return { noun: 'Practice entry', approved: 'Practice entry approved', rejected: 'Practice entry rejected' };
};

const scheduledSession = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return 'Next eligible market open';
  return `${new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  })} market open`;
};

const executionWords = (proposal) => {
  const status = String(proposal.execution_status || '').toLowerCase();
  if (proposal.fill_id && proposal.fill_price && proposal.filled_quantity) return 'Virtual fill recorded';
  if (status === 'filled') return 'Execution record needs review — fill details are unavailable';
  if (status === 'failed') return 'Virtual execution failed';
  if (status === 'cancelled') return 'Virtual execution cancelled';
  if (status === 'claimed') return 'Virtual execution in progress';
  if (status === 'pending') return 'Awaiting virtual execution';
  return 'Execution status unavailable';
};

const recentDecisionSummary = (proposal) => {
  const parts = [proposal.status.replaceAll('_', ' ')];
  const executionStatus = String(proposal.execution_status || '').toLowerCase();
  const hasFill = Boolean(proposal.fill_id && proposal.fill_price && proposal.filled_quantity);
  if (proposal.authorized_entry_notional_cap) {
    parts.push(`approved up to ${money(proposal.authorized_entry_notional_cap, proposal.currency)}`);
    if (['pending', 'claimed'].includes(executionStatus)) parts.push('final shares at next eligible open');
  }
  if (proposal.status === 'APPROVED' && proposal.position_effect === 'CLOSE') {
    parts.push(proposal.quantity_override_classification === 'USER_MODIFIED_QUANTITY'
      ? `modified close approved for ${practiceShares(proposal.authorized_quantity)} shares`
      : 'full position close approved');
  }
  if (executionStatus) parts.push(executionStatus === 'filled' && !hasFill
    ? 'fill status needs review' : `execution ${executionStatus}`);
  if (hasFill) parts.push(`${practiceShares(proposal.filled_quantity)} shares at ${money(proposal.fill_price, proposal.currency)} · ${money(proposal.notional, proposal.currency)} virtual notional`);
  return parts.join(' · ');
};

const toneClasses = {
  emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300',
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const brokerMarketStatusAvailable = (market) => (
  market?.available === true
  || (market?.available !== false && typeof market?.is_open === 'boolean')
);

const TodayPage = () => {
  const { token, isTokenVerified } = useContext(AuthContext);
  const enabled = Boolean(token && isTokenVerified);
  const location = useLocation();
  const requestedProposalId = new URLSearchParams(location.search).get('proposal');
  const queryClient = useQueryClient();
  const [decisionError, setDecisionError] = useState('');
  const [entryAmounts, setEntryAmounts] = useState({});
  const [entryErrors, setEntryErrors] = useState({});
  const [pendingDecision, setPendingDecision] = useState(null);
  const [decisionConfirmation, setDecisionConfirmation] = useState(null);
  const [openingDecision, setOpeningDecision] = useState(false);
  const decisionDialogRef = useRef(null);
  const decisionConfirmButtonRef = useRef(null);
  const decisionTriggerRef = useRef(null);
  const decisionConfirmationRef = useRef(null);
  const focusedProposalLocationRef = useRef('');

  const marketQuery = useQuery({
    queryKey: ['today-market'],
    queryFn: async () => (await axiosInstance.get('/api/get-market-status')).data,
    enabled,
    retry: 1,
    refetchInterval: 60000,
  });

  const botsQuery = useQuery({
    queryKey: ['today-bots'],
    queryFn: async () => {
      const bots = (await axiosInstance.get('/api/bots')).data || [];
      const runtimePairs = await Promise.all(bots
        .filter((bot) => bot.status === 'ready')
        .map(async (bot) => {
          try {
            return [bot.id, (await axiosInstance.get(`/api/bots/${bot.id}/runtime/dry-run`)).data];
          } catch (error) {
            if (error.response?.status === 404) return [bot.id, null];
            return [bot.id, { error: true }];
          }
        }));
      const practicePairs = await Promise.all(bots
        .filter((bot) => bot.status === 'ready')
        .map(async (bot) => {
          try {
            return [bot.id, (await axiosInstance.get(`/api/bots/${bot.id}/runtime/paper`)).data];
          } catch (error) {
            if (error.response?.status === 404) return [bot.id, null];
            return [bot.id, { error: true }];
          }
        }));
      return {
        bots,
        runtimes: Object.fromEntries(runtimePairs),
        practiceRuntimes: Object.fromEntries(practicePairs),
      };
    },
    enabled,
    retry: 1,
    refetchInterval: 10000,
  });

  const archivedPracticeQuery = useQuery({
    queryKey: ['today-archived-practice'],
    queryFn: async () => {
      const bots = ((await axiosInstance.get('/api/bots?include_archived=true')).data || [])
        .filter((bot) => bot.status === 'archived');
      const snapshots = Object.fromEntries(await Promise.all(bots.map(async (bot) => {
        try {
          return [bot.id, (await axiosInstance.get(`/api/bots/${bot.id}/runtime/paper`)).data];
        } catch (error) {
          if (error.response?.status === 404) return [bot.id, null];
          return [bot.id, { error: true }];
        }
      })));
      return archivedPracticePositions(bots, snapshots);
    },
    enabled,
    retry: 1,
    refetchInterval: 60000,
  });

  const liveActivityQuery = useBotLiveActivity(
    enabled, null, botsQuery.data?.bots?.map((bot) => bot.id) || [],
  );

  const attentionQuery = useQuery({
    queryKey: ['today-attention'],
    queryFn: async () => (await axiosInstance.get('/api/today/attention')).data,
    enabled,
    retry: 1,
    refetchInterval: 15000,
  });

  const proposalsQuery = useQuery({
    queryKey: ['practice-proposals'],
    queryFn: async () => (await axiosInstance.get('/api/practice/proposals?limit=100')).data,
    enabled,
    retry: 1,
    refetchInterval: 15000,
  });

  const exactProposalQuery = useQuery({
    queryKey: ['practice-proposal', requestedProposalId],
    queryFn: async () => (await axiosInstance.get(`/api/practice/proposals/${requestedProposalId}`)).data,
    enabled: enabled && Boolean(requestedProposalId) && proposalsQuery.isSuccess
      && !(proposalsQuery.data?.proposals || []).some((proposal) => proposal.id === requestedProposalId),
    retry: false,
  });

  const decisionMutation = useMutation({
    mutationFn: async ({ proposalId, decision, entryNotionalCap, closeQuantity }) => (await axiosInstance.post(
      `/api/practice/proposals/${proposalId}/decision`,
      {
        decision,
        idempotency_key: crypto.randomUUID(),
        ...(entryNotionalCap ? { entry_notional_cap: entryNotionalCap } : {}),
        ...(closeQuantity ? { close_quantity: closeQuantity } : {}),
      },
    )).data,
    onMutate: () => setDecisionError(''),
    onSuccess: async (result, variables) => {
      const proposal = variables.proposal;
      const words = decisionWords(proposal);
      setPendingDecision(null);
      setDecisionConfirmation({
        proposal,
        decision: variables.decision,
        entryNotionalCap: variables.entryNotionalCap,
        closeQuantity: result.authorized_close_quantity || variables.closeQuantity,
        quantityOverrideClassification: result.quantity_override_classification,
        remainingQuantity: variables.remainingQuantity,
        title: variables.decision === 'APPROVED'
          ? result.quantity_override_classification === 'USER_MODIFIED_QUANTITY'
            ? `Modified ${words.noun} approved`
            : words.approved
          : words.rejected,
      });
      if (result.status === variables.decision) {
        await Promise.all([
          queryClient.cancelQueries({ queryKey: ['practice-proposals'] }),
          queryClient.cancelQueries({ queryKey: ['today-attention'] }),
        ]);
        queryClient.setQueryData(['practice-proposals'], (current) => current && ({
          ...current,
          proposals: current.proposals.map((item) => item.id === proposal.id
            ? {
              ...item,
              status: result.status,
              authorized_entry_notional_cap: variables.decision === 'APPROVED'
                ? result.authorized_entry_notional_cap || variables.entryNotionalCap
                : null,
              authorized_quantity: variables.decision === 'APPROVED'
                ? result.authorized_close_quantity || variables.closeQuantity
                : null,
              quantity_override_classification: result.quantity_override_classification,
            } : item),
          pending_count: Math.max(0, (current.pending_count || 0) - 1),
        }));
        queryClient.setQueryData(['today-attention'], (current) => (
          resolveDecidedProposal(current, proposal)
        ));
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['practice-proposals'] }),
        queryClient.invalidateQueries({ queryKey: ['practice-proposal', proposal.id] }),
        queryClient.invalidateQueries({ queryKey: ['today-bots'] }),
        queryClient.invalidateQueries({ queryKey: ['user-attention'] }),
        queryClient.invalidateQueries({ queryKey: ['today-attention'] }),
      ]);
      requestAnimationFrame(() => decisionConfirmationRef.current?.focus());
    },
    onError: (error) => setDecisionError(
      error.response?.data?.detail?.message
      || 'That decision could not be recorded. Refresh Today and review the current status.',
    ),
  });

  const closeDecisionDialog = useCallback(() => {
    if (decisionMutation.isPending) return;
    setPendingDecision(null);
    setDecisionError('');
    requestAnimationFrame(() => decisionTriggerRef.current?.focus());
  }, [decisionMutation.isPending]);

  useEffect(() => {
    if (!pendingDecision) return undefined;
    decisionConfirmButtonRef.current?.focus();
    const handleDialogKey = (event) => {
      if (event.key === 'Escape') closeDecisionDialog();
      if (event.key !== 'Tab') return;
      const controls = [...(decisionDialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled)') || [])];
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleDialogKey);
    return () => document.removeEventListener('keydown', handleDialogKey);
  }, [closeDecisionDialog, pendingDecision]);

  useEffect(() => {
    const proposalId = new URLSearchParams(location.search).get('proposal');
    if (!proposalId) {
      focusedProposalLocationRef.current = '';
      setOpeningDecision(false);
      return;
    }
    if (botsQuery.isLoading || attentionQuery.isLoading
        || (!proposalsQuery.isSuccess && !proposalsQuery.isError)
        || (exactProposalQuery.isPending && exactProposalQuery.fetchStatus !== 'idle')) return;
    const locationKey = `${location.search}${location.hash}`;
    if (focusedProposalLocationRef.current === locationKey) return;
    const target = document.getElementById(`practice-proposal-${proposalId}`);
    if (!target) return;
    const alignTarget = () => {
      const bounds = target.getBoundingClientRect();
      if (bounds.top >= window.innerHeight || bounds.bottom <= 0) {
        target.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
      target.focus({ preventScroll: true });
      setOpeningDecision(false);
    };
    const frame = requestAnimationFrame(alignTarget);
    const observer = new ResizeObserver(alignTarget);
    observer.observe(document.body);
    const timeout = window.setTimeout(() => observer.disconnect(), 3000);
    focusedProposalLocationRef.current = locationKey;
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [location.hash, location.search, botsQuery.isLoading, attentionQuery.isLoading,
    proposalsQuery.data, proposalsQuery.isError, proposalsQuery.isSuccess,
    exactProposalQuery.data, exactProposalQuery.isPending, exactProposalQuery.fetchStatus]);

  if (botsQuery.isLoading || attentionQuery.isLoading) {
    return <div className="min-h-screen bg-slate-50 dark:bg-slate-950"><NavigationBar /><div className="pt-28"><LoadingSpinner /></div></div>;
  }

  const bots = botsQuery.data?.bots || [];
  const runtimes = botsQuery.data?.runtimes || {};
  const practiceRuntimes = botsQuery.data?.practiceRuntimes || {};
  const practiceOverview = practicePortfolioOverview(bots, practiceRuntimes);
  const archivedPractice = archivedPracticeQuery.data || { portfolios: [], unavailableCount: 0 };
  const marketAccessState = marketQuery.data?.market_data_access_state
    || (marketQuery.isError ? MARKET_DATA_ACCESS.PROVIDER_UNAVAILABLE : null);
  const readinessByBot = Object.fromEntries(bots.map((bot) => [
    bot.id,
    presentTodayBotReadiness(bot, runtimes[bot.id], marketAccessState),
  ]));
  const working = bots.filter((bot) => isTruthfullyWatching(
    bot,
    runtimes[bot.id],
    readinessByBot[bot.id],
  ));
  const started = bots.filter((bot) => runtimes[bot.id]?.desired_state === 'run');
  const paused = bots.filter((bot) => runtimes[bot.id]?.desired_state === 'pause');
  const listedProposals = proposalsQuery.data?.proposals || [];
  const proposals = exactProposalQuery.data
    && !listedProposals.some((proposal) => proposal.id === exactProposalQuery.data.id)
    ? [...listedProposals, exactProposalQuery.data] : listedProposals;
  const focusedProposal = proposals.find((proposal) => proposal.id === requestedProposalId);
  const attentionItems = attentionQuery.isError ? [] : attentionQuery.data?.items || [];
  const canonicalProposalIds = new Set(attentionItems
    .filter((item) => item.source_type === 'PRACTICE_PROPOSAL').map((item) => item.id));
  const pendingProposals = proposals.filter((proposal) => proposal.status === 'PENDING'
    && (canonicalProposalIds.has(proposal.id) || proposal.id === requestedProposalId));
  const recentDecisions = proposals.filter((proposal) => proposal.status !== 'PENDING').slice(0, 6);
  if (focusedProposal && focusedProposal.status !== 'PENDING'
      && !recentDecisions.some((proposal) => proposal.id === focusedProposal.id)) {
    recentDecisions.unshift(focusedProposal);
    recentDecisions.pop();
  }
  const marketAvailable = brokerMarketStatusAvailable(marketQuery.data);
  const marketOpen = marketAvailable && marketQuery.data.is_open === true;
  const hasMarketDataDemand = bots.some((bot) => (
    botRequiresMarketData(bot, runtimes[bot.id])
  ));
  const marketBanner = !marketAvailable && hasMarketDataDemand
    ? marketDataBanner(marketAccessState)
    : null;
  const readModelAttention = attentionQuery.isError;
  const customerSeverity = readModelAttention
    ? CUSTOMER_ATTENTION_SEVERITY.ATTENTION
    : attentionQuery.data?.severity || CUSTOMER_ATTENTION_SEVERITY.OK;
  const attentionPresentation = customerAttentionPresentation(customerSeverity);
  const attentionCount = attentionQuery.data?.open_total || 0;
  const proposalAttention = new Map(attentionItems
    .filter((item) => item.source_type === 'PRACTICE_PROPOSAL')
    .map((item) => [item.id, { severity: item.severity }]));
  const needsAttention = customerSeverity !== CUSTOMER_ATTENTION_SEVERITY.OK;
  const greeting = new Date().getHours() < 12 ? 'Good morning.' : new Date().getHours() < 18 ? 'Good afternoon.' : 'Good evening.';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <NavigationBar />
      {pendingDecision && (() => {
        const {
          proposal, decision, entryNotionalCap, closeMode, closeQuantity,
        } = pendingDecision;
        const isApproval = decision === 'APPROVED';
        const isClose = proposal.position_effect === 'CLOSE';
        const words = decisionWords(proposal);
        const closeVerb = proposal.semantic_action === 'exit_short' ? 'Cover' : 'Close';
        const suggested = parsePracticeQuantity(proposal.proposed_quantity);
        const selectedClose = parsePracticeQuantity(closeQuantity);
        const isModified = isApproval && isClose && closeMode === 'ADJUST'
          && selectedClose && suggested && selectedClose.micros < suggested.micros;
        const closeQuantityValid = !isApproval || !isClose || (
          selectedClose && suggested && selectedClose.micros > 0n
          && selectedClose.micros <= suggested.micros
        );
        const remainingQuantity = remainingPracticeShares(
          proposal.proposed_quantity,
          closeQuantity,
        );
        const canAdjustClose = isApproval && isClose
          && proposal.close_quantity_contract_version === 'practice-close-quantity-v1'
          && proposal.reason_code !== 'runtime_safety_exit';
        const approximateValue = Number(closeQuantity || proposal.proposed_quantity || 0)
          * Number(proposal.reference_price || 0);
        const confirmDecision = () => {
          if (!closeQuantityValid) {
            setDecisionError(`Enter more than 0 and no more than ${practiceShares(proposal.proposed_quantity)} shares, using up to six decimal places.`);
            requestAnimationFrame(() => document.getElementById('practice-close-quantity')?.focus());
            return;
          }
          decisionMutation.mutate({
            ...pendingDecision,
            closeQuantity: isApproval && isClose ? selectedClose.canonical : null,
            remainingQuantity,
          });
        };
        return (
          <div className="fixed inset-0 z-[80] flex items-end justify-center overflow-y-auto bg-slate-950/70 p-4 sm:items-center" role="presentation">
            <section
              ref={decisionDialogRef}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="practice-decision-dialog-title"
              aria-describedby="practice-decision-dialog-description"
              className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl dark:bg-slate-900 sm:p-6"
            >
              <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Pinngro Practice</p>
              <h2 id="practice-decision-dialog-title" className="mt-2 text-2xl font-black">
                {isApproval
                  ? `Approve ${isModified ? 'modified ' : ''}${words.noun} for ${proposal.symbol}?`
                  : `Reject ${words.noun} for ${proposal.symbol}?`}
              </h2>
              <div id="practice-decision-dialog-description" className="mt-4 space-y-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {canAdjustClose && (
                  <fieldset className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                    <legend className="px-1 font-black text-slate-900 dark:text-white">Your approval</legend>
                    <div className="mt-1 grid gap-2 sm:grid-cols-2">
                      <button type="button" aria-pressed={closeMode !== 'ADJUST'} onClick={() => { setDecisionError(''); setPendingDecision((current) => ({ ...current, closeMode: 'AS_SUGGESTED', closeQuantity: suggested.canonical })); }} className={`min-h-11 rounded-xl border px-4 py-3 text-left font-bold ${closeMode !== 'ADJUST' ? 'border-blue-600 bg-blue-50 text-blue-950 ring-2 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-100' : 'border-slate-300 dark:border-slate-700'}`}>Approve as suggested</button>
                      <button type="button" aria-pressed={closeMode === 'ADJUST'} onClick={() => { setDecisionError(''); setPendingDecision((current) => ({ ...current, closeMode: 'ADJUST' })); requestAnimationFrame(() => document.getElementById('practice-close-quantity')?.focus()); }} className={`min-h-11 rounded-xl border px-4 py-3 text-left font-bold ${closeMode === 'ADJUST' ? 'border-blue-600 bg-blue-50 text-blue-950 ring-2 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-100' : 'border-slate-300 dark:border-slate-700'}`}>Adjust amount</button>
                    </div>
                    {closeMode === 'ADJUST' && (
                      <div className="mt-4">
                        <label htmlFor="practice-close-quantity" className="font-bold text-slate-900 dark:text-white">Shares to close</label>
                        <input id="practice-close-quantity" type="text" inputMode="decimal" value={closeQuantity || ''} aria-invalid={!closeQuantityValid} aria-describedby="practice-close-quantity-help" onChange={(event) => { setDecisionError(''); setPendingDecision((current) => ({ ...current, closeQuantity: event.target.value })); }} className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 font-black text-slate-950 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white" />
                        <p id="practice-close-quantity-help" className="mt-2 text-xs text-slate-500">Enter up to {practiceShares(proposal.proposed_quantity)} shares, using no more than six decimal places.</p>
                        {isModified && <p className="mt-2 font-bold text-amber-800 dark:text-amber-200">You have changed the amount suggested by the strategy.</p>}
                      </div>
                    )}
                  </fieldset>
                )}
                {isClose ? (
                  <dl className="grid gap-3 rounded-xl bg-slate-50 p-4 dark:bg-slate-800 sm:grid-cols-2">
                    <div><dt className="font-bold text-slate-500">Strategy suggested</dt><dd className="font-black text-slate-900 dark:text-white">{closeVerb} {practiceShares(proposal.proposed_quantity)} shares</dd></div>
                    <div><dt className="font-bold text-slate-500">Your approval</dt><dd className="font-black text-slate-900 dark:text-white">{closeVerb} {practiceShares(closeQuantity || proposal.proposed_quantity)} shares</dd></div>
                    <div><dt className="font-bold text-slate-500">Remaining after successful fill</dt><dd className="font-black text-slate-900 dark:text-white">{remainingQuantity === null ? 'Enter a valid amount' : `${practiceShares(remainingQuantity)} shares`}</dd></div>
                    {proposal.reference_price && <div><dt className="font-bold text-slate-500">Approximate value</dt><dd className="font-black text-slate-900 dark:text-white">{money(approximateValue, proposal.currency)}</dd></div>}
                    <div><dt className="font-bold text-slate-500">Planned execution</dt><dd className="font-black text-slate-900 dark:text-white">Next eligible market open</dd></div>
                  </dl>
                ) : (
                  <dl className="grid gap-3 rounded-xl bg-slate-50 p-4 dark:bg-slate-800 sm:grid-cols-2">
                    <div><dt className="font-bold text-slate-500">Virtual amount</dt><dd className="font-black text-slate-900 dark:text-white">{money(entryNotionalCap, proposal.currency)}</dd></div>
                    <div><dt className="font-bold text-slate-500">Planned execution</dt><dd className="font-black text-slate-900 dark:text-white">Next eligible market open</dd></div>
                  </dl>
                )}
                {isApproval ? isModified ? (
                  <p>This changes the quantity suggested by the strategy. If the virtual exit fills successfully, the remaining Practice position will stay open and continue to be evaluated. If the exit conditions remain true, Pinngro may later suggest another exit for the remaining position.</p>
                ) : (
                  <p>Approval allows Pinngro Practice to attempt this action at the scheduled time. The final simulated fill price is determined by the existing Practice execution rules; a fill is not guaranteed.</p>
                ) : isClose ? (
                  <p>Rejecting this suggestion leaves the current Practice position open. Pinngro will continue evaluating it and may generate another exit suggestion later.</p>
                ) : (
                  <p>Rejecting this suggestion cancels this Practice entry. No virtual position will be opened from this proposal.</p>
                )}
                {decisionError && <p role="alert" className="rounded-xl bg-rose-100 p-3 font-bold text-rose-800 dark:bg-rose-950/60 dark:text-rose-200">{decisionError}</p>}
              </div>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button type="button" onClick={closeDecisionDialog} disabled={decisionMutation.isPending} className="min-h-11 rounded-xl border border-slate-300 px-5 py-3 font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">Cancel</button>
                <button
                  ref={decisionConfirmButtonRef}
                  type="button"
                  disabled={decisionMutation.isPending}
                  onClick={confirmDecision}
                  className={`${isApproval ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-700 hover:bg-rose-800'} min-h-11 rounded-xl px-5 py-3 font-black text-white disabled:opacity-50`}
                >
                  {decisionMutation.isPending
                    ? 'Recording…'
                    : isApproval
                      ? isModified
                        ? `Approve modified ${words.noun}`
                        : `Approve ${words.noun}`
                      : `Reject ${words.noun}`}
                </button>
              </div>
            </section>
          </div>
        );
      })()}
      <main className="mx-auto max-w-7xl px-4 pb-16 pt-24 sm:px-6 lg:px-8">
        <section className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">Today</p>
            <h1 className="mt-2 text-4xl font-black tracking-tight sm:text-5xl">{greeting}</h1>
            <p className="mt-3 max-w-3xl text-lg text-slate-600 dark:text-slate-300">
              {bots.length === 0
                ? 'Your strategy desk is ready. Create your first bot to begin practicing.'
                : `Your bot team reports here. ${working.length} confirmed watching; ${Math.max(started.length - working.length, 0)} starting or waiting. ${customerSeverity === CUSTOMER_ATTENTION_SEVERITY.URGENT ? 'A time-sensitive Practice decision needs you.' : needsAttention ? 'A setup or Practice item needs your attention.' : 'Suggestions appear only when strategy conditions are met.'}`}
            </p>
            <p className="mt-2 max-w-3xl text-sm font-semibold text-slate-500 dark:text-slate-400">Your bots watch the market, apply your strategy, and bring suggestions to Today. You approve every Practice action.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-sm font-bold">
            <span className="rounded-full bg-blue-100 px-4 py-2 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300"><FaFlask className="mr-2 inline" />PINNGRO PRACTICE</span>
            <span className={`rounded-full px-4 py-2 ${marketAvailable && marketOpen ? toneClasses.emerald : toneClasses.slate}`}><FaClock className="mr-2 inline" />{marketAvailable ? `MARKET ${marketOpen ? 'OPEN' : 'CLOSED'}` : 'BROKER MARKET STATUS UNAVAILABLE'}</span>
          </div>
        </section>

        {marketBanner && (
          <section role="status" className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
            <p className="font-bold">{marketBanner.title}</p>
            <p className="mt-1 leading-6">{marketBanner.message}</p>
            {marketBanner.cta && <Link to={marketBanner.destination} className="mt-3 inline-flex rounded-lg bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">{marketBanner.cta}</Link>}
          </section>
        )}

        <section
          className={`mt-8 rounded-3xl border-2 shadow-sm ${!readModelAttention && attentionCount === 0 ? 'p-5' : 'p-6 sm:p-8'} ${attentionPresentation.sectionClass}`}
          aria-labelledby="customer-attention-heading"
          aria-label={`Today status: ${attentionPresentation.label}`}
          data-customer-severity={customerSeverity}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className={`text-xs font-black uppercase tracking-[0.18em] ${attentionPresentation.labelClass}`}>
                {attentionPresentation.label}
              </p>
              <h2 id="customer-attention-heading" className={`mt-2 font-black ${!readModelAttention && attentionCount === 0 ? 'text-xl' : 'text-3xl'}`}>
                {readModelAttention ? 'Attention status unavailable' : attentionCount === 0 ? 'All caught up' : attentionPresentation.heading}
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700 dark:text-slate-200">
                {readModelAttention ? 'Pinngro could not verify open items. Refresh Today before relying on this status.' : attentionCount === 0 ? 'No decisions or setup steps need you right now.' : attentionPresentation.description}
              </p>
            </div>
            <span
              role="status"
              aria-live="polite"
              aria-label={readModelAttention ? 'Attention status unavailable' : `${attentionCount} open customer ${attentionCount === 1 ? 'action' : 'actions'}`}
              className={`self-start rounded-full px-3 py-1.5 text-xs font-black ${attentionPresentation.badgeClass}`}
            >
              {readModelAttention ? 'STATUS UNAVAILABLE' : `${attentionCount} OPEN`}
            </span>
          </div>

          {readModelAttention && (
            <article role="alert" className="mt-6 rounded-2xl border border-amber-300 bg-white p-5 dark:border-amber-800 dark:bg-slate-900">
              <h3 className="font-black">Today status is unavailable</h3>
              <p className="mt-2 text-sm">Pinngro cannot verify your open attention right now. Refresh Today or try again later.</p>
            </article>
          )}
          {!readModelAttention && attentionItems.length > 0 && (
            <div className="mt-6 grid gap-3 md:grid-cols-2" data-testid="today-open-attention-items">
              {attentionItems.map((item) => (
                <article key={item.id} className="rounded-2xl border border-amber-300 bg-white p-5 dark:border-amber-800 dark:bg-slate-900">
                  <p className="text-xs font-black uppercase tracking-wider text-amber-700 dark:text-amber-300">{item.label} · {item.severity}</p>
                  <h3 className="mt-2 text-xl font-black">{item.symbol ? `${item.symbol} · ` : ''}{item.bot_name}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-200">{item.reason}</p>
                  {item.evidence_session && <p className="mt-2 text-xs text-slate-500">Evidence session: {item.evidence_session}</p>}
                  <Link to={item.action_path} className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700">{item.action_label} <FaArrowRight /></Link>
                </article>
              ))}
            </div>
          )}

          {decisionError && <p role="alert" className="mt-5 rounded-xl bg-rose-100 p-4 text-sm font-bold text-rose-800 dark:bg-rose-950/60 dark:text-rose-200">{decisionError}</p>}
          {decisionConfirmation && (() => {
            const {
              proposal, decision, title, closeQuantity,
              quantityOverrideClassification, remainingQuantity,
            } = decisionConfirmation;
            const isClose = proposal.position_effect === 'CLOSE';
            const isApproval = decision === 'APPROVED';
            const isModified = quantityOverrideClassification === 'USER_MODIFIED_QUANTITY';
            return (
              <div ref={decisionConfirmationRef} tabIndex="-1" role="status" aria-live="polite" className="mt-5 rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-emerald-950 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-100">
                <p className="text-lg font-black">{title}</p>
                <p className="mt-1 font-bold">
                  {proposal.symbol} — {isClose
                    ? isApproval
                      ? `${practiceShares(closeQuantity || proposal.proposed_quantity)} virtual shares`
                      : 'current Practice position remains open'
                    : money(decisionConfirmation.entryNotionalCap, proposal.currency)}
                </p>
                <p className="mt-2 text-sm leading-6">
                  {isApproval
                    ? isModified
                      ? `${practiceShares(remainingQuantity)} shares will remain open if the scheduled virtual exit fills successfully.`
                      : 'Scheduled for the next eligible market open. Approval authorizes a later Practice attempt; it does not guarantee a fill.'
                    : isClose
                      ? 'Pinngro will continue evaluating this position and may suggest another exit later.'
                      : 'No virtual position will be opened from this proposal.'}
                </p>
                <Link
                  to={`/dashboard?proposal=${proposal.id}#practice-proposal-${proposal.id}`}
                  onClick={() => {
                    setOpeningDecision(true);
                    const target = document.getElementById(`practice-proposal-${proposal.id}`);
                    if (target) {
                      target.scrollIntoView({ block: 'center' });
                      target.focus({ preventScroll: true });
                      setOpeningDecision(false);
                    }
                  }}
                  className="mt-3 inline-flex min-h-11 items-center gap-2 text-sm font-black underline"
                >
                  {openingDecision ? 'Opening decision…' : `View ${isApproval ? 'approved action' : 'recorded decision'}`} <FaArrowRight />
                </Link>
              </div>
            );
          })()}
          {requestedProposalId && !focusedProposal && exactProposalQuery.isFetching && (
            <p role="status" className="mt-5 text-sm font-bold">Opening the exact Practice decision…</p>
          )}
          {requestedProposalId && !focusedProposal && (proposalsQuery.isError || exactProposalQuery.isError) && (
            <div id={`practice-proposal-${requestedProposalId}`} tabIndex="-1" role="alert" className="mt-5 rounded-2xl border border-amber-300 bg-amber-50 p-5 text-amber-950 focus:outline-none focus:ring-2 focus:ring-amber-500 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              <p className="font-black">This Practice decision could not be shown.</p>
              <p className="mt-2 text-sm">{exactProposalQuery.error?.response?.status === 404 ? 'This exact decision is missing or inaccessible to this account.' : 'Pinngro could not load this Practice decision. Try again.'}</p>
              <button type="button" onClick={() => { proposalsQuery.refetch(); exactProposalQuery.refetch(); }} className="mt-3 min-h-11 rounded-lg border border-amber-500 px-3 py-2 text-sm font-bold">Retry loading</button>
            </div>
          )}
          {proposalsQuery.isLoading ? (
            <div className="mt-6"><LoadingSpinner size="sm" /></div>
          ) : pendingProposals.length > 0 && (
            <div className="mt-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-lg font-black">Practice decisions</h3>
                <span className="rounded-full bg-slate-200 px-3 py-1.5 text-xs font-black text-slate-800 dark:bg-slate-800 dark:text-slate-100">{pendingProposals.length} PENDING</span>
              </div>
              <div className="mt-5 grid gap-5 lg:grid-cols-2">
              {pendingProposals.map((proposal) => {
                const ownedBot = bots.find((item) => item.id === proposal.bot_id);
                const proposalSeverity = proposalAttention.get(proposal.id) || {
                  severity: proposal.customer_attention_severity || CUSTOMER_ATTENTION_SEVERITY.ATTENTION,
                  label: proposal.customer_attention_label || 'Practice decision',
                };
                const proposalPresentation = customerAttentionPresentation(
                  proposalSeverity.severity,
                );
                const proposalBot = {
                  id: proposal.bot_id,
                  name: proposal.bot_name,
                  strategy_name: proposal.strategy_name,
                  trading_strategy_family: ownedBot?.trading_strategy_family,
                  evaluator_version: ownedBot?.evaluator_version,
                };
                const isEntry = proposal.position_effect === 'OPEN';
                const currency = proposal.currency || 'USD';
                const maximumEntry = Number(proposal.maximum_entry_notional || 0);
                const amountValue = entryAmounts[proposal.id] ?? (maximumEntry ? maximumEntry.toFixed(2) : '');
                const selectedAmount = Number(amountValue);
                const amountValid = !isEntry || (Number.isFinite(selectedAmount) && selectedAmount > 0 && selectedAmount <= maximumEntry);
                const estimatedShares = isEntry && proposal.reference_price && amountValid
                  ? selectedAmount / Number(proposal.reference_price)
                  : null;
                const approve = (trigger) => {
                  if (isEntry && !amountValid) {
                    setEntryErrors((current) => ({ ...current, [proposal.id]: `Enter an amount greater than zero and no more than ${money(maximumEntry, currency)}.` }));
                    requestAnimationFrame(() => document.getElementById(`entry-amount-${proposal.id}`)?.focus());
                    return;
                  }
                  setEntryErrors((current) => ({ ...current, [proposal.id]: '' }));
                  decisionTriggerRef.current = trigger;
                  setDecisionError('');
                  setPendingDecision({
                    proposalId: proposal.id,
                    decision: 'APPROVED',
                    entryNotionalCap: isEntry ? selectedAmount.toFixed(2) : null,
                    closeMode: isEntry ? null : 'AS_SUGGESTED',
                    closeQuantity: isEntry
                      ? null
                      : parsePracticeQuantity(proposal.proposed_quantity)?.canonical,
                    proposal,
                  });
                };
                const reject = (trigger) => {
                  decisionTriggerRef.current = trigger;
                  setDecisionError('');
                  setPendingDecision({
                    proposalId: proposal.id,
                    decision: 'REJECTED',
                    entryNotionalCap: null,
                    closeMode: null,
                    closeQuantity: null,
                    proposal,
                  });
                };
                const explanation = proposal.decision_explanation || {
                  summary: proposal.explanation,
                  evidence_points: [],
                };
                return (
                <article
                  id={`practice-proposal-${proposal.id}`}
                  key={proposal.id}
                  tabIndex="-1"
                  className={`min-w-0 rounded-2xl border bg-white p-5 shadow-sm dark:bg-slate-900 ${proposalPresentation.itemClass}`}
                  data-customer-severity={proposalSeverity.severity}
                  aria-label={`${proposalSeverity.label}: ${proposal.symbol}`}
                >
                  <p className={`text-xs font-black uppercase tracking-[0.16em] ${proposalPresentation.labelClass}`}>
                    {proposalSeverity.label}
                  </p>
                  <BotIdentity bot={proposalBot} />
                  <p className="mt-4 text-xs font-black uppercase tracking-[0.16em] text-slate-500">What Pinngro suggests</p>
                  <p className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-200">{practiceSignalTitle(proposal)}</p>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="mt-3 text-sm font-black text-slate-700 dark:text-slate-200">{practiceActionLabel(proposal)} · PINNGRO PRACTICE</p>
                      <h3 className="mt-1 text-2xl font-black">{proposal.company_name || proposal.symbol}</h3>
                      {proposal.company_name && <p className="mt-1 text-sm font-bold text-slate-600 dark:text-slate-300">{proposal.symbol}</p>}
                    </div>
                    <FaExclamationTriangle className={`text-xl ${proposalPresentation.iconClass}`} aria-hidden="true" />
                  </div>
                  <dl className="mt-5 grid min-w-0 gap-3 break-words text-sm sm:grid-cols-2">
                    <div><dt className="font-bold text-slate-500">Find stocks</dt><dd className="mt-1 font-semibold">{proposal.stock_selection_mode === 'ALL_STOCKS' ? 'All Stocks — chosen directly by you' : proposal.stock_focus_name || 'Context unavailable'}</dd></div>
                    <div><dt className="font-bold text-slate-500">Trading Strategy</dt><dd className="mt-1 font-semibold">{strategyDisplayName(proposal.strategy_family, proposal.strategy_name)}</dd></div>
                    {isEntry ? <>
                      <div><dt className="font-bold text-slate-500">Maximum allocation</dt><dd className="mt-1 font-semibold">Up to {Number(proposal.proposed_allocation_pct)}%</dd></div>
                      <div><dt className="font-bold text-slate-500">Maximum virtual amount</dt><dd className="mt-1 font-semibold">{money(maximumEntry, currency)}</dd></div>
                      <div><dt className="font-bold text-slate-500">Available virtual cash</dt><dd className="mt-1 font-semibold">{money(proposal.virtual_cash_at_proposal, currency)}</dd></div>
                      {proposal.position_direction === 'LONG' && <div><dt className="font-bold text-slate-500">Estimated remaining cash</dt><dd className="mt-1 font-semibold">{money(Math.max(0, Number(proposal.virtual_cash_at_proposal) - (Number.isFinite(selectedAmount) ? selectedAmount : 0)), currency)}</dd></div>}
                      {proposal.reference_price && <div><dt className="font-bold text-slate-500">Latest reference price</dt><dd className="mt-1 font-semibold">{money(proposal.reference_price, currency)} <span className="font-normal text-slate-500">(completed market data)</span></dd></div>}
                      <div><dt className="font-bold text-slate-500">Estimated shares</dt><dd className="mt-1 font-semibold">{estimatedShares ? estimatedShares.toLocaleString(undefined, { maximumFractionDigits: 6 }) : 'Final shares will be calculated at the next eligible market open.'}</dd></div>
                    </> : <>
                      <div><dt className="font-bold text-slate-500">Position to close</dt><dd className="mt-1 font-semibold">{proposal.position_direction === 'SHORT' ? 'CLOSE ENTIRE SHORT POSITION' : 'CLOSE ENTIRE POSITION'}</dd></div>
                      <div><dt className="font-bold text-slate-500">Shares</dt><dd className="mt-1 font-semibold">{practiceShares(proposal.proposed_quantity)} virtual shares</dd></div>
                      {proposal.reference_price && <div><dt className="font-bold text-slate-500">Approximate value</dt><dd className="mt-1 font-semibold">{money(Number(proposal.proposed_quantity) * Number(proposal.reference_price), currency)}</dd></div>}
                    </>}
                    <div><dt className="font-bold text-slate-500">Timing</dt><dd className="mt-1 font-semibold">Next eligible market open</dd></div>
                    <div><dt className="font-bold text-slate-500">Approve before</dt><dd className="mt-1 font-semibold">{new Date(proposal.decision_deadline).toLocaleString()}</dd></div>
                  </dl>
                  {isEntry && <div className="mt-5">
                    <label htmlFor={`entry-amount-${proposal.id}`} className="block text-sm font-black">Amount to use</label>
                    <div className="mt-2 flex max-w-sm items-center rounded-xl border border-slate-300 bg-white px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 dark:border-slate-700 dark:bg-slate-950">
                      <span className="shrink-0 text-sm font-bold text-slate-500" aria-hidden="true">{currency}</span>
                      <input id={`entry-amount-${proposal.id}`} type="number" inputMode="decimal" min="0.01" max={maximumEntry} step="0.01" value={amountValue} aria-describedby={`entry-amount-help-${proposal.id}${entryErrors[proposal.id] ? ` entry-amount-error-${proposal.id}` : ''}`} aria-invalid={Boolean(entryErrors[proposal.id])} onChange={(event) => { setEntryAmounts((current) => ({ ...current, [proposal.id]: event.target.value })); setEntryErrors((current) => ({ ...current, [proposal.id]: '' })); }} className="min-w-0 flex-1 bg-transparent px-3 py-3 font-black outline-none" />
                    </div>
                    <p id={`entry-amount-help-${proposal.id}`} className="mt-2 text-xs leading-5 text-slate-500">You can reduce this amount, but cannot exceed {money(maximumEntry, currency)}. Actual shares and fill price are determined at the next eligible market open.</p>
                    {entryErrors[proposal.id] && <p id={`entry-amount-error-${proposal.id}`} role="alert" className="mt-2 text-sm font-bold text-rose-700 dark:text-rose-300">{entryErrors[proposal.id]}</p>}
                  </div>}
                  <div className="mt-5 rounded-xl bg-blue-50 p-4 text-sm leading-6 text-slate-700 dark:bg-blue-950/30 dark:text-slate-200">
                    <h4 className="font-black text-slate-900 dark:text-white">Why is Pinngro suggesting this?</h4>
                    <p className="mt-2">{explanation.summary}</p>
                    {(explanation.evidence_points || []).length > 0 && (
                      <div className="mt-4 border-t border-blue-200 pt-3 dark:border-blue-900">
                        <p className="font-black">Supporting market and strategy evidence</p>
                        <ul className="mt-2 list-disc space-y-1 pl-5">
                          {explanation.evidence_points.map((point) => <li key={point}>{point}</li>)}
                        </ul>
                        {explanation.evidence_session_date && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Based on completed market data through {new Date(`${explanation.evidence_session_date}T00:00:00Z`).toLocaleDateString()}.</p>}
                      </div>
                    )}
                  </div>
                  <TradeExplanation proposalId={proposal.id} compact />
                  <div className="mt-4 grid gap-3 text-sm leading-6 sm:grid-cols-2">
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
                      <p className="font-black">What happens if I approve?</p>
                      <p className="mt-1">{isEntry ? 'Pinngro Practice will attempt the approved virtual entry at the next eligible market open.' : proposal.reason_code === 'runtime_safety_exit' ? `Pinngro Practice will attempt to close the entire ${practiceShares(proposal.proposed_quantity)}-share virtual position at the next eligible market open.` : `You can approve the suggested ${practiceShares(proposal.proposed_quantity)} shares or reduce the amount. Pinngro Practice will attempt the approved virtual exit at the next eligible market open.`} A fill is not guaranteed.</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                      <p className="font-black">What happens if I reject?</p>
                      <p className="mt-1">{isEntry ? 'This suggestion is cancelled and no virtual position opens from it.' : 'The current Practice position remains open. Pinngro will continue evaluating it and may suggest another exit later.'}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm font-bold text-amber-800 dark:text-amber-200">Nothing happens unless you approve. Approval permits a later virtual attempt; it does not guarantee a fill.</p>
                  {proposal.position_direction === 'SHORT' && <p className="mt-3 rounded-xl bg-rose-50 p-3 text-sm font-bold leading-6 text-rose-900 dark:bg-rose-950/40 dark:text-rose-100">{isEntry && Number(proposal.proposed_allocation_pct) > 10 ? `Your bot allows up to ${Number(proposal.proposed_allocation_pct)}%, but Practice short positions are currently limited to 10%. ` : ''}Practice only and fully collateralized; short-sale proceeds remain restricted. A short loses when price rises. No Alpaca Paper or Live order is submitted.</p>}
                  <p className="mt-3 text-xs leading-5 text-slate-500">This suggestion expires at the scheduled market-open deadline if it has not been approved in time.</p>
                  <details className="mt-4 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                    <summary className="cursor-pointer font-bold">Technical evidence</summary>
                    <div className="mt-3 space-y-2 text-xs text-slate-600 dark:text-slate-300">
                      <p>Evaluator finding: {proposal.evaluation_reason}</p>
                      <p>Triggered rules: {(proposal.triggered_rules || []).join(', ')}</p>
                      <p>Completed market evidence: {proposal.market_snapshot_id}</p>
                      <p>Strategy version: {proposal.evaluator_version} · {proposal.strategy_hash}</p>
                      <p>Evaluation: {proposal.originating_evaluation_id}</p>
                      <Link to={`/bots/${proposal.bot_id}/report`} className="inline-flex items-center gap-2 font-bold text-blue-700 dark:text-blue-300">Open bot report <FaArrowRight /></Link>
                    </div>
                  </details>
                  <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                    <button type="button" disabled={decisionMutation.isPending} onClick={(event) => approve(event.currentTarget)} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-black text-white hover:bg-emerald-700 disabled:opacity-50"><FaCheckCircle />Approve virtual action</button>
                    <button type="button" disabled={decisionMutation.isPending} onClick={(event) => reject(event.currentTarget)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-rose-300 px-5 py-3 font-black text-rose-700 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/40"><FaTimesCircle />Reject</button>
                  </div>
                </article>
                );
              })}
              </div>
            </div>
          )}
        </section>

        <section className="mt-8 overflow-hidden rounded-3xl bg-slate-950 text-white shadow-xl dark:border dark:border-slate-800">
          <div className="grid gap-8 px-6 py-8 sm:px-10 lg:grid-cols-[1.1fr_0.9fr] lg:px-12 lg:py-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">Practice overview</p>
              {practiceOverview.kind === 'none' && (
                <div>
                  <p className="mt-4 text-3xl font-black sm:text-4xl">
                    {practiceOverview.unavailableCount > 0 ? 'Practice figures unavailable'
                      : archivedPractice.portfolios.length > 0 ? 'No active Practice portfolio' : 'No Practice portfolio yet'}
                  </p>
                  <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300">
                    {practiceOverview.unavailableCount > 0
                      ? 'Pinngro could not verify the internal virtual records just now. Open Your Bots and try again.'
                      : archivedPractice.portfolios.length > 0
                        ? 'An archived bot has a stored open virtual position below. Its recorded value is not a current market valuation.'
                        : 'Create a bot, prepare its market data, and explicitly start Practice to create its isolated virtual portfolio.'}
                  </p>
                </div>
              )}
              {practiceOverview.kind === 'one' && (
                <div>
                  <p className="mt-4 text-sm font-bold text-cyan-200">{practiceOverview.portfolio.botName} · internal virtual portfolio</p>
                  <p className="mt-2 text-xs font-bold uppercase tracking-wide text-slate-300">Virtual equity</p>
                  <p className="mt-1 text-4xl font-black sm:text-5xl">{money(practiceOverview.portfolio.equity)}</p>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
                    <span>Cash <strong className="text-white">{money(practiceOverview.portfolio.cash)}</strong></span>
                    <span>Realized P&amp;L <strong className={practiceOverview.portfolio.realizedPnl < 0 ? 'text-rose-300' : 'text-emerald-300'}>{money(practiceOverview.portfolio.realizedPnl)}</strong></span>
                    <span>Positions <strong className="text-white">{practiceOverview.portfolio.positionCount}</strong></span>
                  </div>
                  <Link to={`/bots/${practiceOverview.portfolio.botId}/report`} className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-cyan-200 hover:text-white">Open this bot’s Practice portfolio <FaArrowRight /></Link>
                </div>
              )}
              {practiceOverview.kind === 'multiple' && (
                <div>
                  <p className="mt-4 text-3xl font-black sm:text-4xl">{practiceOverview.portfolios.length} isolated bot portfolios</p>
                  <p className="mt-2 text-sm text-slate-300">Pinngro does not combine isolated bot balances into an unsupported account-wide dollar total.</p>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {practiceOverview.portfolios.map((portfolio) => (
                      <Link key={portfolio.botId} to={`/bots/${portfolio.botId}/report`} className="rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/10">
                        <span className="block text-sm font-bold text-cyan-200">{portfolio.botName}</span>
                        <span className="mt-1 block text-xs text-slate-300">Virtual equity</span>
                        <span className="block text-xl font-black">{money(portfolio.equity)}</span>
                        <span className="mt-1 block text-xs text-slate-300">{money(portfolio.cash)} cash · {portfolio.positionCount} positions</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {practiceOverview.kind !== 'none' && practiceOverview.unavailableCount > 0 && (
                <p role="status" className="mt-4 text-sm font-bold text-amber-200">
                  One or more additional Practice portfolios could not be verified just now. Open Your Bots and try again.
                </p>
              )}
              <p className="mt-5 max-w-xl text-sm leading-6 text-slate-300">
                These figures come only from Pinngro’s internal virtual Practice records. Alpaca Paper balances are not included. Practice itself does not submit broker orders.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5"><p className="text-3xl font-black">{working.length}</p><p className="mt-1 text-sm text-slate-300">confirmed watching</p></div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5"><p className="text-3xl font-black">{paused.length}</p><p className="mt-1 text-sm text-slate-300">paused</p></div>
              <div className="col-span-2 rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="flex items-center gap-2 font-bold text-emerald-300"><FaShieldAlt /> Safety status</div>
                <p className="mt-2 text-sm text-slate-300">Practice actions still require your explicit approval. Practice itself does not submit broker orders.</p>
              </div>
            </div>
          </div>
        </section>

        {(archivedPractice.portfolios.length > 0 || archivedPractice.unavailableCount > 0 || archivedPracticeQuery.isError) && (
          <section aria-labelledby="archived-practice-heading" className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-6 text-slate-900 dark:border-amber-900 dark:bg-slate-900 dark:text-slate-100">
            <h2 id="archived-practice-heading" className="text-lg font-black">Archived Practice positions · read-only</h2>
            <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">These bots are archived and cannot run Practice actions. Their stored balances and marks are historical records, not current market valuations.</p>
            {archivedPractice.portfolios.map((portfolio) => (
              <article key={portfolio.botId} className="mt-4 min-w-0 rounded-2xl border border-amber-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                <h3 className="font-bold">{portfolio.botName}</h3>
                <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
                  <div><dt className="text-slate-600 dark:text-slate-300">Stored virtual equity</dt><dd className="font-bold">{money(portfolio.equity)}</dd></div>
                  <div><dt className="text-slate-600 dark:text-slate-300">Virtual cash</dt><dd className="font-bold">{money(portfolio.cash)}</dd></div>
                  <div><dt className="text-slate-600 dark:text-slate-300">Starting capital</dt><dd className="font-bold">{money(portfolio.startingCapital)}</dd></div>
                </dl>
                <p className="mt-3 text-xs text-amber-800 dark:text-amber-200">Stale stored valuation · record updated {recordedUtc(portfolio.valuationRecordedAt)}. Do not use this as a current price or buying-power estimate.</p>
                <ul className="mt-3 space-y-2 text-sm">
                  {portfolio.positions.map((position) => (
                    <li key={`${position.symbol}-${position.direction}`} className="break-words rounded-lg bg-amber-50 p-2 dark:bg-slate-900">
                      <strong>{position.symbol}</strong> · {position.direction === 'SHORT' ? 'short' : 'long'} {practiceShares(position.quantity)} shares · stored mark {position.markPrice == null ? 'unavailable' : money(position.markPrice)} · recorded {recordedUtc(position.recordedAt)}
                    </li>
                  ))}
                </ul>
                <Link to={`/bots/${portfolio.botId}/report`} className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-blue-700 hover:underline dark:text-blue-300">View archived Practice record <FaArrowRight /></Link>
              </article>
            ))}
            {(archivedPractice.unavailableCount > 0 || archivedPracticeQuery.isError) && (
              <p role="status" className="mt-4 text-sm font-semibold text-amber-900 dark:text-amber-200">Some archived Practice records could not be verified. Try again or open the bot’s report.</p>
            )}
          </section>
        )}

        <section className="mt-8">
          <div className="flex items-end justify-between">
            <div><p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Your strategy team</p><h2 className="mt-2 text-3xl font-black">Your bots</h2></div>
            <Link to="/bots" className="inline-flex items-center gap-2 text-sm font-bold text-blue-700 hover:text-blue-900 dark:text-blue-300">Manage and create bots <FaArrowRight /></Link>
          </div>
          {bots.length === 0 ? (
            <div className="mt-5 flex flex-col items-start gap-4 rounded-3xl border border-dashed border-slate-300 bg-white p-8 dark:border-slate-700 dark:bg-slate-900 sm:flex-row sm:items-center">
              <FaRobot className="text-4xl text-slate-400" />
              <div className="flex-1"><h3 className="text-xl font-black">No bots yet</h3><p className="mt-1 text-slate-500">Choose how to find stocks and a Trading Strategy, assign stocks, then explicitly start Pinngro Practice. No real broker order is created.</p></div>
              <Link to="/bots#hire" className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700">Create your first bot</Link>
            </div>
          ) : (
            <div data-testid="today-your-bots-grid" className="mt-5 grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {bots.map((bot) => {
                  const state = readinessByBot[bot.id];
                  return (
                  <article data-testid="today-bot-summary-card" key={bot.id} className="min-w-0 break-words rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <BotIdentity bot={bot} wrapText />
                    <BotLiveActivity bot={liveActivityQuery.data?.bots?.find((item) => item.bot_id === bot.id)} identityBot={bot} query={liveActivityQuery} compact />
                    <p className="mt-4 min-w-0 break-words text-sm text-slate-500"><strong className="text-slate-700 dark:text-slate-200">Watches:</strong> {(bot.symbols || []).join(', ') || 'No stocks assigned'}</p>
                    <p className="mt-2 text-sm text-slate-500"><strong className="text-slate-700 dark:text-slate-200">{bot.stock_selection_mode === 'ALL_STOCKS' ? 'Find stocks:' : 'Stock Focus:'}</strong> {bot.stock_selection_mode === 'ALL_STOCKS' ? 'All Stocks — chosen directly by you' : bot.template_name}</p>
                    <p className="mt-1 text-sm text-slate-500"><strong className="text-slate-700 dark:text-slate-200">Trading Strategy:</strong> {strategyDisplayName(bot.trading_strategy_family, bot.trading_strategy_name)}</p>
                    <p className="mt-2 text-sm font-bold text-amber-700 dark:text-amber-300">{readModelAttention ? 'Attention unavailable' : `${Number(attentionQuery.data?.per_bot?.[bot.id] || 0)} open attention`}</p>
                    {bot.experimental_strategy && <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs leading-5 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"><strong>{bot.trading_strategy_family === 'trend-pullback-v2-experimental' ? 'Practice only' : bot.experimental_strategy.classification} · {strategyDisplayName(bot.trading_strategy_family, bot.experimental_strategy.label)}</strong><br />{bot.experimental_strategy.summary}</p>}
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4 text-sm dark:border-slate-800"><span className="text-slate-500">Assignment</span><span className="font-bold">{(bot.symbols || []).length} {(bot.symbols || []).length === 1 ? 'symbol' : 'symbols'}</span></div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {state.reconnect && <Link to="/account-settings" className="inline-flex rounded-lg bg-amber-600 px-3 py-2 text-sm font-bold text-white hover:bg-amber-700">Connect / reconnect Alpaca</Link>}
                      <Link to={`/bots/${bot.id}/report`} className="inline-flex rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-blue-700 dark:border-slate-700 dark:text-blue-300">View bot report</Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-3"><FaShieldAlt className="text-emerald-600" /><h2 className="text-2xl font-black">Advanced tools available</h2></div>
          <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">Technical controls and custom strategy tools are available whenever you want them. Every Practice bot can still be paused or ended from Your Bots.</p>
          <Link to="/bots" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700">Review your team <FaArrowRight /></Link>
        </section>

        <PublicDisclosureAlerts />

        <section className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-3"><FaCheckCircle className="text-blue-600" /><div><p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Important activity</p><h2 className="mt-1 text-2xl font-black">Recent Practice decisions and results</h2></div></div>
          {recentDecisions.length === 0 ? <p className="mt-5 text-sm text-slate-500">No completed Practice decisions yet.</p> : (
            <div className="mt-5 divide-y divide-slate-100 dark:divide-slate-800">
              {recentDecisions.map((proposal) => (
                <div id={`practice-proposal-${proposal.id}`} tabIndex="-1" key={proposal.id} className={`rounded-lg py-4 focus:outline-none focus:ring-2 focus:ring-blue-500 ${requestedProposalId === proposal.id ? 'bg-blue-50 px-4 dark:bg-blue-950/40' : ''}`}>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div><p className="font-bold">{practiceActionLabel(proposal)} {proposal.symbol} · {proposal.bot_name}</p><p className="mt-1 text-sm text-slate-500">{recentDecisionSummary(proposal)}</p></div>
                    <Link to={`/bots/${proposal.bot_id}/report`} className="text-sm font-bold text-blue-700 dark:text-blue-300">View report</Link>
                  </div>
                  {requestedProposalId === proposal.id && (
                    <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2" data-testid="focused-practice-decision">
                      {proposalsQuery.isError && <div role="alert" className="sm:col-span-2 font-semibold text-amber-800 dark:text-amber-300">The latest decision status could not be refreshed. Retry Today before relying on this record.</div>}
                      <div><dt className="font-bold text-slate-500">Decision</dt><dd className="mt-1 font-semibold">{proposal.status === 'APPROVED' ? 'Approved' : proposal.status.replaceAll('_', ' ')}</dd></div>
                      <div><dt className="font-bold text-slate-500">Virtual amount</dt><dd className="mt-1 font-semibold">{proposal.position_effect === 'OPEN' ? proposal.authorized_entry_notional_cap ? money(proposal.authorized_entry_notional_cap, proposal.currency) : 'No approved amount recorded' : `${practiceShares(proposal.authorized_quantity || proposal.proposed_quantity)} shares to ${proposal.semantic_action === 'exit_short' ? 'cover' : 'close'}`}</dd></div>
                      <div><dt className="font-bold text-slate-500">Scheduled attempt</dt><dd className="mt-1 font-semibold">{proposal.status === 'APPROVED' ? scheduledSession(proposal.target_execution_session) : 'No approved virtual attempt'}</dd></div>
                      <div><dt className="font-bold text-slate-500">Execution</dt><dd className="mt-1 font-semibold">{proposal.status === 'APPROVED' ? executionWords(proposal) : 'No virtual execution authorized'}</dd></div>
                      {proposal.fill_id && proposal.fill_price && proposal.filled_quantity && <div className="sm:col-span-2"><dt className="font-bold text-slate-500">Recorded virtual fill</dt><dd className="mt-1 font-semibold">{practiceShares(proposal.filled_quantity)} shares at {money(proposal.fill_price, proposal.currency)}{proposal.filled_at ? ` · ${new Date(proposal.filled_at).toLocaleString()}` : ''}</dd></div>}
                      {proposal.execution_status === 'failed' && proposal.failure_code && <div className="sm:col-span-2"><dt className="font-bold text-slate-500">Why it failed</dt><dd className="mt-1 font-semibold">{proposal.failure_code.replaceAll('_', ' ')}</dd></div>}
                      {proposal.execution_status === 'cancelled' && proposal.cancellation_reason && <div className="sm:col-span-2"><dt className="font-bold text-slate-500">Why it was cancelled</dt><dd className="mt-1 font-semibold">{proposal.cancellation_reason.replaceAll('_', ' ')}</dd></div>}
                    </dl>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default TodayPage;
